import { useState } from 'react';
import { Modal, Form, InputNumber, DatePicker, Input, Slider, message, Radio, Space } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { tradesApi } from '@tradermind/shared/api';
import type { Trade } from '@tradermind/shared/types';
import dayjs from 'dayjs';

interface CloseTradeModalProps {
  trade: Trade;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CloseTradeModal({ trade, visible, onClose, onSuccess }: CloseTradeModalProps) {
  const { t } = useTranslation('trade');
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [exitPrice, setExitPrice] = useState<number | null>(null);
  const [profitLoss, setProfitLoss] = useState<number>(0);
  const [calcMode, setCalcMode] = useState<'simple' | 'contract'>('simple'); // 计算模式

  const closeMutation = useMutation({
    mutationFn: (data: { exit_price: number; close_time: string; profit_loss: number; post_trade_emotion?: number; notes?: string }) =>
      tradesApi.closeTrade(trade.id, data),
    onSuccess: () => {
      message.success(t('close_modal.success'));
      queryClient.invalidateQueries({ queryKey: ['trades'] });
      queryClient.invalidateQueries({ queryKey: ['trade', String(trade.id)] });
      queryClient.invalidateQueries({ queryKey: ['trade', trade.id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: () => {
      message.error(t('close_modal.failed'));
    },
  });

  // Calculate profit/loss when exit price changes
  const handleExitPriceChange = (value: number | null) => {
    setExitPrice(value);
    if (value) {
      calculateProfitLoss(value);
    }
  };

  // 计算盈亏
  const calculateProfitLoss = (exitPriceValue: number) => {
    const quantity = trade.quantity;
    const entryPrice = trade.entry_price;
    const commission = trade.commission || 0;
    const swap = trade.swap || 0;

    let pl = 0;

    if (calcMode === 'contract') {
      // 合约模式：基于点值计算
      const contractSize = form.getFieldValue('contract_size') || 1;
      const pointValue = form.getFieldValue('point_value') || 1;
      
      const priceDiff = trade.direction === 'long' 
        ? (exitPriceValue - entryPrice) 
        : (entryPrice - exitPriceValue);
      
      // 盈亏 = 价差 × 合约单位 × 点值 × 手数
      pl = priceDiff * contractSize * pointValue * quantity;
    } else {
      // 简单模式：直接价差计算
      if (trade.direction === 'long') {
        pl = (exitPriceValue - entryPrice) * quantity;
      } else {
        pl = (entryPrice - exitPriceValue) * quantity;
      }
    }

    pl = pl - commission - swap;
    setProfitLoss(pl);
    form.setFieldsValue({ profit_loss: pl });
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const data = {
        exit_price: values.exit_price,
        close_time: values.close_time.toISOString(),
        profit_loss: values.profit_loss,
        post_trade_emotion: values.post_trade_emotion,
        notes: values.notes,
      };
      closeMutation.mutate(data);
    });
  };

  return (
    <Modal
      title={t('close_modal.title')}
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={closeMutation.isPending}
      width={600}
    >
      <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
        <div><strong>{t('close_modal.symbol')}: </strong>{trade.symbol}</div>
        <div><strong>{t('close_modal.direction')}: </strong>{trade.direction === 'long' ? t('side.long_full') : t('side.short_full')}</div>
        <div><strong>{t('close_modal.entry_price')}: </strong>{trade.entry_price.toFixed(4)}</div>
        <div><strong>{t('close_modal.quantity')}: </strong>{trade.quantity}</div>
      </div>

      <Form form={form} layout="vertical" initialValues={{ close_time: dayjs(), post_trade_emotion: 5, contract_size: 100, point_value: 1 }}>
        <Form.Item label={t('close_modal.calc_mode')}>
          <Radio.Group value={calcMode} onChange={(e) => {
            setCalcMode(e.target.value);
            if (exitPrice) calculateProfitLoss(exitPrice);
          }}>
            <Space direction="vertical">
              <Radio value="simple">{t('close_modal.simple_mode')}</Radio>
              <Radio value="contract">{t('close_modal.contract_mode')}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {calcMode === 'contract' && (
          <>
            <Form.Item
              label={t('close_modal.contract_size')}
              name="contract_size"
              tooltip={t('close_modal.contract_size_tooltip')}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="100"
                min={0.01}
                onChange={() => exitPrice && calculateProfitLoss(exitPrice)}
              />
            </Form.Item>
            <Form.Item
              label={t('close_modal.point_value')}
              name="point_value"
              tooltip={t('close_modal.point_value_tooltip')}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="1"
                min={0.01}
                onChange={() => exitPrice && calculateProfitLoss(exitPrice)}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          label={t('close_modal.exit_price')}
          name="exit_price"
          rules={[{ required: true, message: t('close_modal.exit_price_required') }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="0.0000"
            precision={4}
            onChange={handleExitPriceChange}
          />
        </Form.Item>

        <Form.Item label={t('close_modal.estimated_pnl')} name="profit_loss">
          <div
            style={{
              padding: 12,
              background: profitLoss >= 0 ? '#f6ffed' : '#fff2f0',
              border: `1px solid ${profitLoss >= 0 ? '#b7eb8f' : '#ffccc7'}`,
              borderRadius: 4,
              fontSize: 24,
              fontWeight: 'bold',
              color: profitLoss >= 0 ? '#52c41a' : '#ff4d4f',
              textAlign: 'center',
            }}
          >
            {profitLoss >= 0 ? '+' : ''}${profitLoss.toFixed(2)}
          </div>
        </Form.Item>

        <Form.Item
          label={t('close_modal.close_time')}
          name="close_time"
          rules={[{ required: true, message: t('close_modal.close_time_required') }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item label={t('close_modal.post_emotion')} name="post_trade_emotion">
          <Slider min={1} max={10} marks={{ 1: t('close_modal.emotion_bad'), 5: '5', 10: t('close_modal.emotion_good') }} />
        </Form.Item>

        <Form.Item label={t('close_modal.notes')} name="notes">
          <Input.TextArea rows={4} placeholder={t('close_modal.notes_placeholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
